import type { FC } from "react"
import { useMemo, useState } from "react"
import SearchSuggestion from "shared/components/SearchSuggestion"

import { useGetTestTemplateSuggestions } from "../../hooks"

import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import { TestTemplateListSearchBy } from "@/graphql/codegen/graphql"
import type { AutocompleteOption } from "@/types/form-fields"
import useDebounce from "@/utils/useDebounce"

interface TestTemplatesSearchSuggestionProps {
  productId: number
  search: string | AutocompleteOption
  onSearch: (searchedItem: string | AutocompleteOption) => void
}

const TestTemplatesSearchSuggestion: FC<TestTemplatesSearchSuggestionProps> = ({
  productId,
  search = "",
  onSearch,
}) => {
  const searchAsString = typeof search === "string" ? search : (search?.label ?? "")
  const [inputValue, setInputValue] = useState(searchAsString)
  const debouncedSearchTerm = useDebounce(inputValue, DEBOUNCE_DELAY_MS)
  const { testTemplates, fetching } = useGetTestTemplateSuggestions({
    query: debouncedSearchTerm,
    searchBy: [TestTemplateListSearchBy.Description, TestTemplateListSearchBy.ExternalSystemId],
    productId,
  })

  const options = useMemo(() => {
    if (fetching) return []
    return (testTemplates?.map((i) => ({
      label: i?.description,
      id: i?.testTemplateId,
    })) || []) as AutocompleteOption[]
  }, [testTemplates, fetching])

  return (
    <SearchSuggestion
      onSearch={onSearch}
      placeholder='Search by Test Description or Test #'
      inputValue={inputValue}
      setInputValue={setInputValue}
      options={options}
      fetching={fetching}
    />
  )
}

export default TestTemplatesSearchSuggestion
